import type { FC, ReactNode } from 'react'

import { Clock, Languages, MapPin } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import PageIntro from '@/components/generals/page-intro'
import Reveal from '@/components/generals/reveal'
import ContactForm from '@/components/contact/contact-form'
import MapSketch from '@/components/contact/map-sketch'
import { ATELIER } from '@/constants/site'

const DETAILS = [
    { icon: MapPin, label: 'Where', lines: [ATELIER.place, ATELIER.region] },
    { icon: Clock, label: 'When', lines: [ATELIER.hours, ATELIER.timezone] },
    { icon: Languages, label: 'Languages', lines: [ATELIER.languages] }
]

const Contact: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contact'
                description="Write to Qog'oz Atelier near Samarkand about orders, wedding stationery, custom paper or a visit to the workshop. We reply within one working day."
            />

            <PageIntro
                id='contact-intro'
                eyebrow='Contact'
                title='Write to the atelier'
                intro='Questions about paper, a custom size for your studio, wedding stationery, or a visit while you are in Samarkand. A real person reads every note.'
            />

            <section id='contact-form' aria-label='Send a message' className='container-page pb-20 md:pb-32'>
                <div className='grid gap-16 lg:grid-cols-12 lg:gap-8'>
                    <div className='order-2 lg:order-1 lg:col-span-5'>
                        <Reveal>
                            <MapSketch />
                        </Reveal>

                        <Reveal delay={120} as='ul' className='mt-12 space-y-7'>
                            {DETAILS.map(({ icon: Icon, label, lines }) => (
                                <li key={label} className='flex gap-4'>
                                    <Icon aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-bark-soft' strokeWidth={1.5} />
                                    <div>
                                        <p className='text-sm font-medium text-bark-soft'>{label}</p>
                                        {lines.map(line => (
                                            <p key={line} className='mt-1 text-[1.02rem] text-bark'>{line}</p>
                                        ))}
                                    </div>
                                </li>
                            ))}
                        </Reveal>
                    </div>

                    <Reveal delay={80} className='order-1 lg:order-2 lg:col-span-6 lg:col-start-7'>
                        <div className='rounded-[3px] border border-bark/12 bg-paper p-6 shadow-[0_24px_50px_-36px_rgba(58,42,31,0.5)] sm:p-9'>
                            <h2 className='font-heading text-[1.9rem] font-light leading-tight text-bark'>
                                Send us a note
                            </h2>
                            <p className='mb-8 mt-2 text-[0.98rem] leading-relaxed text-bark-soft'>
                                We reply within one working day, usually sooner.
                            </p>
                            <ContactForm />
                        </div>
                    </Reveal>
                </div>
            </section>
        </Layout>
    )
}

export default Contact
